import { styled, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from "@mui/material";
import { Column, Footer } from "../shared/types";
import { useMemo } from "react";
import { fillColumnProps, fillFooterProps } from "../shared/utils";
import { DataTableEmpty, DataTableError, DataTableLoader } from "./data-table-states";
import RenderFooter from "./data-table-footer";

export interface DataTableContentProps<T = any> {
    columns?: (Column<T> | undefined | null | false)[],
    rows?: T[],
    footers?: (Footer<T> | undefined | null | false)[],

    fit?: "block" | "flex",

    extractRowKey?: (row: T, index: number) => React.Key,

    loading?: boolean,
    error?: boolean | Error | null,

    emptyText?: string,
}

const StyledTableFooter = styled(TableFooter)(({ theme }) => ({
    position: "sticky",
    zIndex: 1,
    bottom: 0,
    backgroundColor: theme.palette.action.hover,
    '& .MuiTableCell-root': {
        borderBottom: 0,
        color: theme.palette.text.secondary,
        fontWeight: 600,
    }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: "1px",
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    }
}));

const defaultKeyExtractor = (row: any, index: number) => {
    const baseId = row?.id ?? row?.Id ?? row?.ID;
    return baseId !== undefined && baseId !== null ? `${baseId}-${index}` : index;
};

export default function DataTableContent<T = any>({
    columns: receivedColumns = [],
    rows = [],
    footers: receivedFooters = [],

    fit = "block",

    extractRowKey = defaultKeyExtractor,

    loading = false,
    error = false,

    emptyText = "No hay ninguna entrada."
}: DataTableContentProps<T>) {
    const columns = useMemo(() => (
        receivedColumns.filter(c => Boolean(c))
            .map(fillColumnProps)
            .filter(c => c.show)
    ), [receivedColumns]);

    const footers = useMemo(() => (
        receivedFooters.filter(Boolean).map(fillFooterProps)
    ), [receivedFooters]);

    const renderHeaders = useMemo(() => {
        return columns.map((c, ci) => (
            <TableCell key={c.key || ci} align={c.align}
                sx={{ width: c.width === "auto" ? "1px" : c.width, whiteSpace: "nowrap" }}>
                {c.header}
            </TableCell>
        ))
    }, [columns])

    const renderBody = useMemo(() => {
        return rows.map((r, ri) => (
            <StyledTableRow key={extractRowKey(r, ri)}>
                {columns.map((c, ci) => (
                    <TableCell sx={{ whiteSpace: c.width === "auto" ? "nowrap" : undefined }} align={c.align} key={c.key || ci}>
                        {c.render(r, ri)}
                    </TableCell>
                ))}
            </StyledTableRow>
        ))
    }, [columns, rows])

    /*
    const renderFooter = useMemo(() => {
        if (footers.length <= 0) return;

        return <StyledTableFooter>
            <TableRow>
                {footers.map((f, fi) => (
                    <TableCell key={f.key || fi} align={f.align} colSpan={f.columnSpan}>
                        {f.render(rows)}
                    </TableCell>
                ))}
            </TableRow>
        </StyledTableFooter>
    }, [footers])
    */

    const renderFooter = useMemo(() => {
        if (footers.length <= 0) return;

        return <StyledTableFooter>
            <TableRow>
                <RenderFooter rows={rows} columns={columns} footers={footers} />
            </TableRow>
        </StyledTableFooter>
    }, [columns, rows, footers]);

    const renderState = () => {
        if (error) return <DataTableError error={error} />
        if (loading) return <DataTableLoader />
        if (rows.length <= 0) return <DataTableEmpty emptyText={emptyText} />
    }

    return <TableContainer sx={fit === "flex" ? { flex: "1 1 0", display: "flex", flexDirection: "column" } : undefined}>
        <Table stickyHeader size="small" sx={{ flex: 1 }}>
            <TableHead>
                <TableRow>
                    {renderHeaders}
                </TableRow>
            </TableHead>
            <TableBody >
                {!loading && !error && renderBody}
                <TableRow />
            </TableBody>
            {renderFooter}
        </Table>
        {renderState()}
    </TableContainer>
}